/**
 * Shared KPI / breakdown primitives.
 *
 * These used to live as private helpers in DemoExperience.tsx but the
 * session-detail page (and any future per-thing analytics surface) wants
 * the same visual vocabulary. Lifted here verbatim so the band on the
 * Demo experience card and the band on the session overview render
 * identically.
 */
import type React from 'react';

// ─── Skeleton placeholder ──────────────────────────────────────────────
//
// Tiny pulsing bar for in-flight rows in breakdown tables. Kept in this
// file so BreakdownTable has no cross-file dependency for its loading
// state.

export function SkeletonLine({
  width = '100%',
  className = '',
}: {
  width?: string;
  className?: string;
}) {
  return (
    <div
      className={`h-3 bg-soft rounded animate-pulse ${className}`}
      style={{ width }}
    />
  );
}

// ─── KPI tile ──────────────────────────────────────────────────────────

export function KPITile({
  label,
  value,
  hint,
  delta,
  deltaHint,
  deltaIntent = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  /**
   * Optional comparison vs the equivalent prior window. `null` means
   * "no prior data" — renders as `(new)` so the tile doesn't
   * misleadingly show ▲100 % for first-ever sessions on a brand-new
   * device. `flat` renders nothing (zero info value).
   */
  delta?: { pct: number; direction: 'up' | 'down' | 'flat' } | null;
  /**
   * Hover-revealed help text on the delta chip explaining what the
   * percentage compares against (e.g. "vs previous 7 d: 8 sessions").
   * Renders via native `title` so it works without bringing in a
   * tooltip library.
   */
  deltaHint?: string;
  /**
   * Whether an "up" delta is good (more sessions = good) or bad (more
   * downtime = bad). Drives the colour, not the glyph.
   */
  deltaIntent?: 'good-up' | 'bad-up' | 'neutral';
}) {
  let deltaColor = 'text-muted';
  if (delta && delta.direction !== 'flat') {
    const isUp = delta.direction === 'up';
    if (deltaIntent === 'good-up') deltaColor = isUp ? 'text-accent' : 'text-warn';
    else if (deltaIntent === 'bad-up') deltaColor = isUp ? 'text-warn' : 'text-accent';
  }
  let deltaLabel: React.ReactNode = null;
  if (delta === null) {
    deltaLabel = (
      <span
        className={`text-[11px] text-muted italic ${deltaHint ? 'cursor-help' : ''}`}
        title={deltaHint}
      >
        (new)
      </span>
    );
  } else if (delta && delta.direction !== 'flat') {
    const glyph = delta.direction === 'up' ? '▲' : '▼';
    deltaLabel = (
      <span
        className={`text-[11px] tabular-nums ${deltaColor} ${deltaHint ? 'cursor-help' : ''}`}
        title={deltaHint}
      >
        {glyph} {Math.abs(delta.pct).toFixed(0)} %
      </span>
    );
  }
  return (
    <div className="flex-1 px-4 py-4">
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">
        {label}
      </div>
      <div className="flex items-baseline gap-2 mt-1.5">
        <div className="font-mono tabular-nums text-3xl text-fg leading-none">
          {value}
        </div>
        {deltaLabel}
      </div>
      {hint && (
        <div className="text-[10px] text-muted mt-1.5 tabular-nums">{hint}</div>
      )}
    </div>
  );
}

// ─── Breakdown table ───────────────────────────────────────────────────

export interface BreakdownColumn<T> {
  header: string;
  /**
   * Optional hover-revealed help text on the column header, shown via
   * native `title`. Use for jargon ("Impressions", "Active", "Avg")
   * that needs disambiguation against an adjacent column's wording.
   */
  headerTitle?: string;
  align?: 'left' | 'right';
  mono?: boolean;
  /** Max ch width — applies to a `truncate` so long values clip with ellipsis. */
  maxChars?: number;
  render: (row: T) => React.ReactNode;
}

export function BreakdownTable<T>({
  title,
  rows,
  rowKey,
  columns,
  emptyText,
  loading = false,
}: {
  /** Optional section heading above the table. Empty string skips the heading. */
  title: string;
  rows: T[];
  rowKey: (row: T) => string;
  columns: Array<BreakdownColumn<T>>;
  emptyText: string;
  loading?: boolean;
}) {
  // Skip the heading when no title was passed — call sites that already
  // own the section header (e.g. DeviceSessionTimeline's <h2> per
  // section) would otherwise render an empty muted bar above the table.
  const showTitle = title.trim().length > 0;
  // Avoid rendering a <thead> when every column omits its header (rare
  // but valid — keeps the existing visual for any legacy unlabeled use).
  const showHeader = columns.some((c) => c.header && c.header.length > 0);
  return (
    <div className="flex-1 min-w-0">
      {showTitle && (
        <h3 className="text-[10px] uppercase tracking-widest text-muted mb-2">
          {title}
        </h3>
      )}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <SkeletonLine key={i} width={`${100 - i * 10}%`} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted">{emptyText}</p>
      ) : (
        <table className="w-full text-xs">
          {showHeader && (
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                {columns.map((c, i) => (
                  <th
                    key={i}
                    className={[
                      'pb-1.5 font-medium',
                      i > 0 ? 'pl-2' : '',
                      c.align === 'right' ? 'text-right' : 'text-left',
                      c.headerTitle ? 'cursor-help' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    title={c.headerTitle}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((r) => (
              <tr
                key={rowKey(r)}
                className="border-b border-border last:border-0"
              >
                {columns.map((c, i) => (
                  <td
                    key={i}
                    className={[
                      'py-1.5',
                      i > 0 ? 'pl-2' : '',
                      c.align === 'right' ? 'text-right tabular-nums' : '',
                      c.mono ? 'font-mono' : '',
                      c.maxChars ? 'truncate' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={c.maxChars ? { maxWidth: `${c.maxChars}ch` } : undefined}
                  >
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
